import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { App, Button, Form, List, Space, Spin, Typography } from 'antd';
import CardLayout from '../../../layouts/CardLayout';
import { ContractorForm } from '../../../components/Contractor/ContractorForm';
import { CONTRACTOR } from '../../../graphql/queries/contractors';
import {
  CONTRACTOR_MEMBERS_UPDATE,
  CONTRACTOR_UPDATE,
} from '../../../graphql/mutations/contractors';
import { ContractorFormValues } from '../../../types/contractor';
import { UserSearchSelect } from '../../../components/Users/UserSearchSelect';

const ContractorEdit: React.FC = () => {
  const { id } = useParams();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [form] = Form.useForm<ContractorFormValues>();
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);

  const { data, loading, refetch } = useQuery(CONTRACTOR, {
    variables: { id },
    skip: !id,
  });

  const contractor = data?.contractor;

  useEffect(() => {
    if (contractor) {
      form.setFieldsValue(contractor);
    }
  }, [contractor, form]);

  const [updateContractor, { loading: updating }] = useMutation(CONTRACTOR_UPDATE, {
    onCompleted: result => {
      if (result?.contractorUpdate) {
        message.success('Contractor updated successfully');
        refetch();
      }
    },
    onError: error => {
      message.error(error.message || 'Failed to update contractor');
    },
  });

  const [updateMembers, { loading: updatingMembers }] = useMutation(CONTRACTOR_MEMBERS_UPDATE, {
    onCompleted: result => {
      if (result?.contractorMembersUpdate) {
        message.success('Members updated');
        setSelectedMemberId(null);
        refetch();
      }
    },
    onError: error => {
      message.error(error.message || 'Failed to update members');
    },
  });

  const members = contractor?.members || [];
  const memberIdSet = useMemo(
    () => new Set(members.map((member: { id: string }) => member.id)),
    [members]
  );

  if (loading) {
    return <Spin size="large" />;
  }

  if (!contractor) {
    message.error('Contractor not found');
    navigate('/admin/contractors');
    return null;
  }

  const handleFinish = (values: ContractorFormValues) => {
    updateContractor({
      variables: {
        id,
        input: values,
      },
    });
  };

  const handleAddMember = () => {
    if (!selectedMemberId) return;
    updateMembers({
      variables: {
        id,
        addUserIds: [selectedMemberId],
      },
    });
  };

  const handleRemoveMember = (memberId: string) => {
    updateMembers({
      variables: {
        id,
        removeUserIds: [memberId],
      },
    });
  };

  return (
    <CardLayout>
      <ContractorForm
        form={form}
        loading={updating}
        onFinish={handleFinish}
        submitText="Update Contractor"
        initialValues={contractor}
      />
      <div style={{ marginTop: 24 }}>
        <Typography.Title level={4}>Members</Typography.Title>
        <Space direction="vertical" style={{ width: '100%' }}>
          <UserSearchSelect
            value={selectedMemberId || undefined}
            onChange={value => setSelectedMemberId(value || null)}
            excludeUserIds={[...memberIdSet]}
            placeholder="Search user by name or email"
          />
          <Button
            type="primary"
            onClick={handleAddMember}
            disabled={!selectedMemberId}
            loading={updatingMembers}>
            Add member
          </Button>
        </Space>
        <List
          style={{ marginTop: 16 }}
          dataSource={members}
          locale={{ emptyText: 'No members found' }}
          renderItem={(member: { id: string; name: string; email: string }) => {
            const isOwnerMember = contractor.ownerUserId === member.id;
            return (
              <List.Item
                actions={[
                  <Button
                    key="remove"
                    danger
                    size="small"
                    disabled={isOwnerMember}
                    onClick={() => handleRemoveMember(member.id)}
                    loading={updatingMembers}>
                    Remove
                  </Button>,
                ]}>
                <List.Item.Meta title={member.name} description={member.email} />
              </List.Item>
            );
          }}
        />
      </div>
    </CardLayout>
  );
};

export default ContractorEdit;
